import Card from 'components/Card/Card'
import LoadMoreCard from 'components/LoadMoreCard/LoadMoreCard'
import SectionContent from 'components/Section/SectionContent'
import SkeletonCard from 'components/Skeleton/SkeletonCard'
import API, { API_TIME_OUT } from 'core/api'
import { getAccessToken, getPartnerId, getUA } from 'core/singleton'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAsync } from 'react-async'
import styled from 'styled-components'
import { getConfig } from 'core/singleton'
import { useRecoilValue } from 'recoil'
import { getLocation, requestBridge, sleep } from 'core/util/default'
import { BRIDGE, ONLY_POST_MSG_PARTNERS } from 'core/constants/bridge'
import { IS_OPEN_LINK, STORE_ROOT_PROPS } from 'core/store/global'
import { getExecutorForPartner } from 'core/util/partner'
import { getUrlQueryParameter, moveToLink, parseObjectToQueryString } from 'core/util/url'
import { filterObject } from 'core/util/object'
import { IconChevronDown } from 'components/Icons'

import { useLocation } from 'react-router-dom'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { CUSTOM_CLASS_NAME, LINK_MOVE_TYPE } from 'core/constants'
import { STORE_AIRBRIDGE, STORE_MARKETING_GTM } from 'core/store/analysis'
import SectionTitle from 'components/Section/SectionTitle'
import SectionLayout from 'container/SectionLayout'
import { BroadcastParams } from '@types'
import SectionBottom from 'components/Section/SectionBottom'
import Loading from 'components/Loading/Loading'

interface SectionBroadCastProps {
  displayType?: 'horizontal' | 'vertical'
  title?: string
}

const SectionBroadCast: React.FC<SectionBroadCastProps> = ({
  displayType = 'horizontal',
  title,
}) => {
  const [nextKey, setNextKey] = useState<string | undefined>('init')
  const [broadcastItem, setBroadcastItem] = useState<BroadcastItem[]>([])
  const [loadData, setLoadData] = useState<boolean>(true)
  const { broadcastLimit } = getConfig()
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)
  const partnerId = getPartnerId()
  const isBridge = root?.bridge
  const query = getUrlQueryParameter()
  const location = useLocation()
  const { isSDK } = getUA()

  const { data, isLoading } = useAsync({
    promiseFn: API.getBroadcast,
    nextKey,
    limit: broadcastLimit,
    watch: nextKey,
  })

  /**
   * @name EventHandler
   **/
  const handleLoadMore = useCallback(() => {
    const nextKey = data && data.nextKey && JSON.stringify(data.nextKey)
    if (nextKey) {
      setNextKey(nextKey)
    }
    new GAHelper().sendEvent(GA_EVENT.CLICK_MORE, {
      partnersId: partnerId,
      location: getLocation(location),
      position: 'latestLive',
    })
  }, [data, location, partnerId])

  const handleClickBroadcast = useCallback(
    ({
        link,
        broadcastId,
        broadcastTypeCode,
        broadcastName,
      }: {
        link?: string
        broadcastId?: string
        isBridge?: boolean
        broadcastTypeCode?: string
        broadcastName?: string
        order?: number
      }) =>
      () => {
        if (!link) return

        if (marketingGTM?.checkAvailability()) {
          const queryStringPrefix = link.indexOf('?') >= 0 ? '&' : '?'
          link = link.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
        }

        const params: BroadcastParams = {
          broadcastUrl: link,
        }
        if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
          params.broadcastId = broadcastId
        }

        new GAHelper().sendEvent(GA_EVENT.CLICK_CONTENT, {
          partnersId: partnerId,
          location: getLocation(location),
          broadcastTypeCode: broadcastTypeCode,
          broadcastName: broadcastName,
          position: 'latestLive',
        })

        if (getExecutorForPartner(partnerId)?.reqMoveBroadcastBridge?.(link)) {
          return
        }
        if (root?.bridge || window[BRIDGE.BROADCAST] || isSDK) {
          requestBridge(BRIDGE.BROADCAST, params)
        } else {
          moveToLink({
            airbridge,
            link,
            type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
          })
        }
      },
    [airbridge, isOpenLink, location, marketingGTM, partnerId, root?.bridge, isSDK],
  )

  const handleClickProduct = useCallback(
    async ({
      broadcastId,
      link,
      productId,
      roomId,
    }: {
      broadcastId: string
      link: string
      productId: string
      roomId: string
    }) => {
      if (productId && broadcastId) {
        const resProductUrl = await API.getTrackingProductUrl({
          broadcastId,
          productId,
          roomId,
        })

        if (resProductUrl) {
          let productUrl = resProductUrl.redirectUrl

          if (marketingGTM?.checkAvailability()) {
            let queryStringPrefix = productUrl.indexOf('?') >= 0 ? '&' : '?'
            productUrl = productUrl.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
            queryStringPrefix = link.indexOf('?') >= 0 ? '&' : '?'
            link = link.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
          }

          productUrl =
            getExecutorForPartner(partnerId)?.appendSearchParamsToProductUrl?.(new URL(productUrl))
              .href ?? productUrl

          if (root?.bridge || window[BRIDGE.PRODUCT] || isSDK) {
            const res = requestBridge(BRIDGE.PRODUCT, {
              productId,
              productUrl,
              broadcastUrl: link,
            })
            if (res) return true
          }

          if (
            (root?.partnerId === 'lg-uplus' ||
              root?.partnerId === 'tbhshop' ||
              root?.partnerId === 'shinsegaelive') &&
            productUrl
          ) {
            productUrl = productUrl.concat(`&broadcastId=${broadcastId}`)
          }

          moveToLink({
            airbridge,
            link: productUrl,
            type: isOpenLink ? LINK_MOVE_TYPE.NEW_WINDOW : LINK_MOVE_TYPE.REDIRECT,
          })
        }
        return true
      }

      await sleep(API_TIME_OUT)

      return true
    },
    [marketingGTM, partnerId, root?.bridge, root?.partnerId, isSDK, airbridge, isOpenLink],
  )

  /**
   * @name DOMElement
   **/
  const skeleton = useMemo(() => {
    if (isLoading && nextKey === 'init') {
      return (
        <>
          <SkeletonCard className={`${CUSTOM_CLASS_NAME.LIVE_CARD_SKELETON}`} />
          <SkeletonCard className={`${CUSTOM_CLASS_NAME.LIVE_CARD_SKELETON}`} />
          <SkeletonCard className={`${CUSTOM_CLASS_NAME.LIVE_CARD_SKELETON}`} />
          <SkeletonCard className={`${CUSTOM_CLASS_NAME.LIVE_CARD_SKELETON}`} />
          <SkeletonCard className={`${CUSTOM_CLASS_NAME.LIVE_CARD_SKELETON}`} />
        </>
      )
    }
  }, [isLoading, nextKey])

  const broadcast = useMemo(() => {
    const token = getAccessToken()

    return broadcastItem.map((item, i) => {
      const {
        broadcastId,
        broadcastName: title,
        broadcastStateCode,
        broadcastThumbnailList,
        collectConfigList,
        outputPath,
        partnerProfileImageUrl: shopLogo,
        previewUrl,
        productListDetail,
        programmingStartDt,
        room,
        roomId,
        shopName,
        totalDuration: playTime,
        usePurchaseEvent: isShowPurchaseAuthBadge,
      } = item
      const incomingCounterInfo = room?.incomingCounterInfo

      const representImage = broadcastThumbnailList?.filter((x) => x.isRepresentative === true)

      const queryString = parseObjectToQueryString({
        accessToken: token,
        npc: 'live',
        ...filterObject(query, (k) => Boolean(k.startsWith('utm'))),
      })
      const link = outputPath + queryString

      const isLive = broadcastStateCode === 'onair'

      const product = productListDetail.find(
        (product) =>
          !product.isSoldout &&
          (product.realTimeProductShow === undefined || product.realTimeProductShow),
      )

      const headerCustomBadgeInfo = collectConfigList?.filter(
        ({ collectType }) => collectType === 'BADGE',
      )?.[0]

      const bottomCustomButtonInfo = collectConfigList?.filter(
        ({ collectType }) => collectType === 'BUTTON',
      )?.[0]

      return (
        <Card
          key={broadcastId}
          className={`${CUSTOM_CLASS_NAME.LIVE_CARD}`}
          link={link}
          isOpenLink={isOpenLink}
          broadcastId={broadcastId}
          broadcastStateCode={broadcastStateCode}
          header={{ customBadgeInfo: headerCustomBadgeInfo, incomingCounterInfo, isLive, playTime }}
          bottom={{ isShowPurchaseAuthBadge }}
          stream={previewUrl ?? undefined}
          thumbnail={`${representImage?.[0]?.thumbnailUrl}?h=732&w=548&f=jpg`}
          info={{
            customButtonInfo: bottomCustomButtonInfo,
            programmingStartDt,
            shopLogo,
            shopName,
            title,
          }}
          product={product}
          root={root}
          handleClickProduct={(params) => handleClickProduct({ ...params, roomId })}
          isBridge={!!isBridge}
          order={i}
          handleClickBroadcast={handleClickBroadcast}
        />
      )
    })
  }, [broadcastItem, root, isOpenLink, handleClickProduct, handleClickBroadcast, isBridge, query])

  const horizontalTypeLoadMoreButton = useMemo(() => {
    if (displayType !== 'horizontal' || !loadData) return null

    return <LoadMoreCard isLoading={isLoading} onClickLoadMore={handleLoadMore} />
  }, [displayType, isLoading, loadData, handleLoadMore])

  const verticalTypeLoadMoreButton = useMemo(() => {
    if (displayType !== 'vertical' || !loadData) return null

    if (isLoading) return <Loading isLoading />
    return (
      <StyledVerticalTypeLoadMore>
        <button
          type={'button'}
          className={'load-more-button'}
          disabled={isLoading}
          onClick={handleLoadMore}
        >
          {'더보기'}
          <IconChevronDown />
        </button>
      </StyledVerticalTypeLoadMore>
    )
  }, [displayType, isLoading, loadData, handleLoadMore])

  /**
   * @name LifeCycleHook
   **/
  useEffect(() => {
    if (data && data.items) {
      setBroadcastItem((prev) => [...prev, ...data.items])
    }
    if (data && !data.nextKey) {
      setLoadData(false)
    }
  }, [data])

  return (
    <SectionLayout
      elementId="broadcast"
      trigger={`${isLoading}_${nextKey}`}
      className={`${CUSTOM_CLASS_NAME.LIVE}`}
    >
      <StyledSectionBroadCast>
        {title && (
          <SectionTitle className={`${CUSTOM_CLASS_NAME.LIVE_TITLE}`}>
            <span>{title}</span>
          </SectionTitle>
        )}
        <SectionContent className={`${CUSTOM_CLASS_NAME.LIVE_CONTENTS} ${displayType}`}>
          {/* 스켈레톤 */}
          {skeleton}
          {/* 방송카드 리스트 */}
          {broadcast}
          {/* 더보기 */}
          {horizontalTypeLoadMoreButton}
        </SectionContent>
        <SectionBottom>{verticalTypeLoadMoreButton}</SectionBottom>
      </StyledSectionBroadCast>
    </SectionLayout>
  )
}

const StyledSectionBroadCast = styled.div`
  width: 100%;
  position: relative;
`

const StyledVerticalTypeLoadMore = styled.div`
  padding-top: 20px;
  width: 100%;

  .load-more-button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 10px 0;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    width: 100%;
    min-height: 40px;
    background-color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    color: #333333;
    cursor: pointer;
  }
`

export default SectionBroadCast
